import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { gtag } from "../../utils";
import { useMount } from "../../hooks";
import "./style.scss";

import { ForwardButton, BackwardButton } from "../../svg-icons/pagination-buttons";
import GridContent from "./componets/GridContent";
import ImageSlide from "./componets/ImageSlide";
import PaginationDots from "./componets/PaginationDots";
import Modal from "../../components/Modal";
import { generateReactKey } from "../../utils/reactKey";

export default function ImageGrid({ rendering, fields, page }) {
  let { headingBody, headingType, body, items, backgroundColor, paddingAboveTop, paddingAboveBottom, alignment, overlineText, gridType } = fields;

  items.map(item => {
    let { coverImage, imageCaption, video, sound, videoAutoPlay, playIcon } = item.fields;
    let image = null;
    if (video.value) {
      image = {
        type: "video",
        title: video.value.title,
        controls: true,
        muted: sound.value,
        autoplay: videoAutoPlay.value,
        videoTitle: video.value.title,

        src: coverImage.value ? coverImage.value.src : "",
        width: coverImage.value.width,
        height: coverImage.value.height,
        caption: imageCaption.value,
        icon: {
          value: (playIcon && playIcon.value && playIcon.value.alt) || ""
        },
        sources: [
          {
            src: video.value.src,
            type: video.value.mimeType
          }
        ]
      };
    } else if (coverImage.value.src) {
      image = {
        type: "image",
        src: coverImage.value.src,
        alt: coverImage.value.alt,
        title: coverImage.value.alt,
        caption: imageCaption.value,
        width: coverImage.value.width,
        height: coverImage.value.height
      };
    }
    item.image = image;
    return item;
  });
  const LANDSCAPE = 1,
    PORTRAIT = 2,
    SQUARE = 3,
    ANY = 4;

  const threshold = 100;

  const [imageGallery, setImageGallery] = useState([]);
  const [carouselOrderListState, setCarouselOrderList] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [shifting, setShifingState] = useState(false);
  const [slideSize, setSlideSize] = useState(0);
  const [allowShift, setAllowShift] = useState(true);
  const [posX1, setPosX1] = useState(0);
  const [posX2, setPosX2] = useState(0);
  const [posInitial, setPosInitial] = useState();
  const [posFinal, setPosFinal] = useState();
  const [showModal, setShowModal] = useState(false);

  const slidesLength = items.length;

  const slidesContainer = useRef();
  const slideWidth = useRef();

  const updateWindowDimensions = useCallback(() => {
    let slideSize = slideWidth.current.offsetWidth + 60;
    setSlideSize(slideSize);
  }, []);

  const createRows = useCallback(classifiedItems => {
    // divide by ratio
    let landscapeImages = classifiedItems.filter(i => i.image.ratio === LANDSCAPE),
      portraitImages = classifiedItems.filter(i => i.image.ratio === PORTRAIT),
      squareImages = classifiedItems.filter(i => i.image.ratio === SQUARE),
      leftoversLandscape = [],
      leftoversPortrait = [],
      textCards = classifiedItems.filter(i => i.image.ratio === ANY);
    let imageGalleryLocal = [],
      portraitLandscapePairs = [],
      squareRows = [];
    let newRow = [];

    // handle squares
    let squareResponse = createSquaresRows(squareImages);
    squareRows = squareResponse.squareRows;

    // handel portrait landscape pairs
    if (landscapeImages.length > portraitImages.length) {
      // more landscape images
      for (let i = 0; i < portraitImages.length; i++) {
        newRow.push(landscapeImages[i]);
        newRow.push(portraitImages[i]);
        portraitLandscapePairs.push(newRow);
        newRow = [];
      }
      leftoversLandscape = landscapeImages.slice(portraitImages.length, landscapeImages.length);
    } else {
      // more portrait images
      for (let i = 0; i < landscapeImages.length; i++) {
        newRow.push(landscapeImages[i]);
        newRow.push(portraitImages[i]);
        portraitLandscapePairs.push(newRow);
        newRow = [];
      }
      leftoversPortrait = portraitImages.slice(landscapeImages.length, portraitImages.length);
    }

    // handle leftovers
    // can only be one square left
    if (squareResponse.leftovers.length === 1) {
      if (leftoversLandscape.length > 0) {
        portraitLandscapePairs.push([leftoversLandscape.pop(), squareResponse.leftovers.pop()]);
      } else if (leftoversPortrait.length > 0) {
        portraitLandscapePairs.push([squareResponse.leftovers.pop(), leftoversPortrait.pop()]);
      } else if (textCards.length > 0) {
        let textItem = textCards.pop();
        let otherItem = squareResponse.leftovers.pop();
        textItem.image = {
          width: otherItem.image.width,
          height: otherItem.image.height
        };
        portraitLandscapePairs.push([otherItem, textItem]);
      } else {
        //signle square is left :/
        // do full width like leftover landscape
        leftoversLandscape.push(squareResponse.leftovers.pop());
      }
    }

    //handle portrait leftovers
    if (leftoversPortrait.length > 1) {
      // many portrait images left -> handle like square
      let result = createSquaresRows(leftoversPortrait);
      squareRows = [squareRows, ...result.squareRows];
    } else if (leftoversPortrait.length === 1) {
      if (textCards.length >= 0) {
        let textItem = textCards.pop();
        let otherItem = leftoversPortrait.pop();
        textItem.image = {
          width: otherItem.image.width,
          height: otherItem.image.height * 0.6
        };
        portraitLandscapePairs.push([otherItem, textItem]);
      } else {
        //signle portrait is left :/
        // do full width like leftover landscape
        leftoversLandscape.push(leftoversPortrait.pop());
      }
    }

    // make single rows of landscape leftovers
    let leftoversLandscapeRows = [];
    leftoversLandscape = [...leftoversLandscape, ...textCards];
    leftoversLandscape.forEach(item => {
      leftoversLandscapeRows.push([item]);
    });

    //flip every second pai
    for (let i = 0; i < portraitLandscapePairs.length; i++) {
      if (i % 2 === 1) {
        portraitLandscapePairs[i] = [portraitLandscapePairs[i][1], portraitLandscapePairs[i][0]];
      }
    }

    // build image gallery rows
    let sortedRowVariants = [];
    if (portraitLandscapePairs.length > squareRows.length) {
      sortedRowVariants.push(portraitLandscapePairs);
      sortedRowVariants.push(squareRows);
    } else {
      sortedRowVariants.push(squareRows);
      sortedRowVariants.push(portraitLandscapePairs);
    }

    if (leftoversLandscapeRows.length > sortedRowVariants[0].length) {
      sortedRowVariants = [leftoversLandscapeRows, ...sortedRowVariants];
    } else if (leftoversLandscapeRows.length < sortedRowVariants[1].length) {
      sortedRowVariants = [...sortedRowVariants, leftoversLandscapeRows];
    } else {
      sortedRowVariants = [sortedRowVariants[0], leftoversLandscapeRows, sortedRowVariants[1]];
    }

    //braid
    for (let i = 0; i < sortedRowVariants[0].length; i++) {
      imageGalleryLocal.push(sortedRowVariants[0][i]);

      if (sortedRowVariants[1][i]) {
        imageGalleryLocal.push(sortedRowVariants[1][i]);
      }

      if (sortedRowVariants[2][i]) {
        imageGalleryLocal.push(sortedRowVariants[2][i]);
      }
    }

    //create one long list for carousel
    let carouselOrderList = [];
    imageGalleryLocal.forEach(row => {
      row.forEach(item => {
        carouselOrderList.push(item);
      });
    });
    return {
      imageGalleryLocal,
      carouselOrderList: carouselOrderListOriginal(imageGalleryLocal)
    };
  }, []);

  const carouselOrderListOriginal = imageGalleryLocal => {
    //create one long list for carousel
    let carouselOrderList = [];

    imageGalleryLocal.forEach(row => {
      row.forEach(item => {
        carouselOrderList.push(item);
      });
    });

    return carouselOrderList;
  };

  const createSquaresRows = items => {
    let newRow = [];
    let galleryRows = [];
    while (items.length >= 1) {
      if (items.length >= 2) {
        if (items.length % 3 === 0 || items.length % 3 === 2) {
          newRow.push(items.pop());
          newRow.push(items.pop());
          if (items.length >= 1) {
            newRow.push(items.pop());
          }
          galleryRows.push(newRow);
          newRow = [];
        } else {
          newRow.push(items.pop());
          newRow.push(items.pop());
          galleryRows.push(newRow);
          newRow = [];
        }
      } else {
        break;
      }
    }
    return { squareRows: galleryRows, leftovers: items };
  };

  const getImageRatio = useCallback(media => {
    let height;
    let width;

    // if (media.type === "video") {
    //   height = media.height;
    //   width = media.width;
    // } else {
    height = media.height;
    width = media.width;
    //   }
    if (height / width >= 1.25) {
      return PORTRAIT;
    } else if (height / width <= 0.67) {
      return LANDSCAPE;
    } else {
      return SQUARE;
    }
  }, []);

  const classifyImagesByRatio = useCallback(() => {
    let classifiedItems = [];
    for (let i = 0; i < items.length; i++) {
      let item = items[i];
      let ratio = item.image ? getImageRatio(item.image) : ANY; // text can be any ratio :)

      let calssifiedItem = {
        ...item,
        image: { ...item.image, ratio: ratio }
      };

      classifiedItems.push(calssifiedItem);
    }

    return classifiedItems;
  }, [items, getImageRatio]);

  const onItemClicked = (_, rowIndex, itemIndex) => {
    var carouselIndex = 0;

    for (var i = 0; i <= rowIndex; i++) {
      if (rowIndex > i) {
        carouselIndex += imageGallery[i].length;
      } else {
        carouselIndex += itemIndex;
      }
    }

    setShowModal(true);
    setCurrentPageIndex(carouselIndex);
  };

  const closeModal = () => {
    // document.body.classList.remove("show-modal");
    setShowModal(false);
    setCurrentPageIndex(undefined);
  };

  // Carousel functinallity
  const shiftSlide = (dir, action) => {
    setShifingState(true);
    let items = slidesContainer.current;

    if (allowShift) {
      if (!action) {
        setPosInitial(items.offsetLeft);
      }

      setCurrentPageIndex(currentPageIndex + dir);

      if (dir === 1) {
        items.style.left = posInitial - slideSize + "px";
      } else if (dir === -1) {
        items.style.left = posInitial + slideSize + "px";
      }
    }
    setAllowShift(false);
  };

  const onSlideForward = () => {
    gtag('event', 'Component_info', {
      'component_name': 'ImageGrid',
      'page_title': page?.name
    });
    shiftSlide(1);
  };

  const onSlideBack = () => {
    gtag('event', 'Component_info', {
      'component_name': 'ImageGrid',
      'page_title': page?.name
    });
    shiftSlide(-1);
  };

  const transitionHasFinished = () => {
    setShifingState(false);
  };

  const checkIfCarouselNeedsToWrap = useCallback(() => {
    var items = slidesContainer.current;

    if (currentPageIndex === -1) {
      items.style.left = -(slidesLength * slideSize) + "px";
      setCurrentPageIndex(slidesLength - 1);
    }

    if (currentPageIndex === slidesLength) {
      items.style.left = -(1 * slideSize) + "px";
      setCurrentPageIndex(0);
    }

    setAllowShift(true);
  }, [currentPageIndex, slideSize, slidesLength]);

  const dragStart = e => {
    let items = slidesContainer.current;
    if (items && items.length <= 1) {
      return;
    }
    e = e || window.event;
    // e.preventDefault(); //neeed to allow button click an verticall scroll
    e.stopPropagation();
    setPosInitial(items.offsetLeft);

    if (e.type === "touchstart") {
      setPosX1(e.touches[0].clientX);
    } else {
      setPosX1(e.clientX);
      document.onMouseUp = dragEnd;
      document.onMouseMove = dragAction;
    }
  };

  const dragAction = e => {
    let items = slidesContainer.current;
    if (items && items.length <= 1) {
      return;
    }
    e = e || window.event;

    if (e.type === "touchmove") {
      setPosX2(posX1 - e.touches[0].clientX);
      setPosX1(e.touches[0].clientX);
    } else {
      setPosX2(posX1 - e.clientX);
      setPosX1(e.clientX);
    }
    items.style.left = items.offsetLeft - posX2 + "px";
  };

  const dragEnd = () => {
    let items = slidesContainer.current;
    setPosFinal(items.offsetLeft);
  };

  useEffect(() => {
    let items = slidesContainer.current;
    if (posFinal - posInitial < -threshold) {
      shiftSlide(1, "drag");
    } else if (posFinal - posInitial > threshold) {
      shiftSlide(-1, "drag");
    } else if (items?.style) {
      items.style.left = posInitial + "px";
    }

    document.onMouseUp = null;
    document.onMouseMove = null;
  }, [posFinal]);

  useEffect(() => {
    if (shifting === false) {
      checkIfCarouselNeedsToWrap();
    }
  }, [shifting, checkIfCarouselNeedsToWrap]);

  useMount(() => {
    let classifiedItems = classifyImagesByRatio();
    let { imageGalleryLocal, carouselOrderList } = createRows(classifiedItems);

    setImageGallery(imageGalleryLocal);
    // Clone first and last slide
    let last = carouselOrderList[carouselOrderList.length - 1];
    let first = carouselOrderList[0];
    carouselOrderList.length ? setCarouselOrderList([last, ...carouselOrderList, first]) : setCarouselOrderList([...carouselOrderList]);

    document.body.classList.remove("show-modal");

    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);

    return function cleanup() {
      window.removeEventListener("resize", updateWindowDimensions);
    };
  });

  const slideClassNames = classNames("slides", {
    shifting: shifting === true
  });

  const slidesStyle = {
    left: `-${(currentPageIndex + 1) * slideSize}px`
  };
  if (!fields) return null;
  // console.log(carouselOrderListState, "carouselOrderListState")

  const gridProps = {
    imageGallery: imageGallery,
    headingBody,
    headingType,
    body,
    items,
    backgroundColor,
    paddingAboveTop,
    paddingAboveBottom,
    alignment,
    overlineText,
    slideWidth: slideWidth,
    onItemClicked: onItemClicked
  };

  return (
    <div className={gridType === "story" ? "grid" : ""} datasourceid={rendering.dataSource}>
      <GridContent {...gridProps} />
      <Modal className="image-grid-modal" showModal={showModal} onCloseClicked={closeModal}>
        <div className="slider full-width grid">
          <PaginationDots pages={items} currentPageIndex={currentPageIndex} />
          <div ref={slidesContainer} className={slideClassNames} style={slidesStyle} onTransitionEnd={transitionHasFinished} onMouseDown={dragStart} onTouchStart={dragStart} onTouchEnd={dragEnd} onTouchMove={dragAction}>
            {carouselOrderListState &&
              carouselOrderListState.map((item, carouselIndex) => {
                return <ImageSlide {...{ ...item.fields, image: item.image }} index={carouselIndex} slideSize={slideSize} key={generateReactKey()} currentPageIndex={currentPageIndex} />;
              })}
          </div>
        </div>
        <span className={"pagination-button back"} onClick={onSlideBack}>
          <BackwardButton />
        </span>
        <span className={"pagination-button forward"} onClick={onSlideForward}>
          <ForwardButton />
        </span>
      </Modal>
    </div>
  );
}

ImageGrid.propTypes = {
  heading: PropTypes.object,
  body: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object)
};
